export interface WalletWithdrawalListItem {
  id: string;
  amount: number;
  status: string;
  createdAt: string;
  estimatedAvailability: string | null;
  destinationLabel: string | null;
}

const IN_PROGRESS_STATUSES = new Set([
  'requested',
  'in_review',
  'processing',
  'awaiting_payment',
  'in_transit',
]);

export function isWithdrawalInProgress(status: string): boolean {
  return IN_PROGRESS_STATUSES.has(status);
}

export function formatWithdrawalStatusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'processing':
      return 'Processing';
    case 'in_review':
      return 'In review';
    case 'in_transit':
      return 'In transit';
    case 'requested':
    case 'awaiting_payment':
      return 'Processing';
    case 'failed':
      return 'Failed';
    case 'canceled':
      return 'Canceled';
    case 'denied':
      return 'Denied';
    case 'reversed':
      return 'Reversed';
    default:
      return status.replace(/_/g, ' ');
  }
}

export function formatWithdrawalTitle(destinationLabel: string | null): string {
  return destinationLabel ? `Withdrawal to ${destinationLabel}` : 'Withdrawal';
}

export function formatWithdrawalSubtitle(status: string): string {
  if (status === 'in_transit' || status === 'processing') {
    return 'On the way to your bank';
  }
  if (status === 'requested' || status === 'in_review' || status === 'awaiting_payment') {
    return 'Usually 1–3 business days';
  }
  if (status === 'failed' || status === 'denied') {
    return 'Update payout details and try again';
  }
  if (status === 'reversed') {
    return 'Funds were returned to your balance';
  }
  return formatWithdrawalStatusLabel(status);
}

export function formatWithdrawalDateLabel(withdrawal: WalletWithdrawalListItem): string {
  const availability = withdrawal.estimatedAvailability
    ? new Date(withdrawal.estimatedAvailability)
    : null;
  const created = new Date(withdrawal.createdAt);

  if (isWithdrawalInProgress(withdrawal.status) && availability) {
    return `Expected ${formatShortDate(availability)}`;
  }
  if (withdrawal.status === 'completed' && availability) {
    return formatShortDate(availability);
  }
  return formatShortDate(created);
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function withdrawalStatusColor(status: string): string {
  if (status === 'completed') {
    return 'green';
  }
  if (status === 'failed' || status === 'denied' || status === 'reversed') {
    return 'red';
  }
  if (isWithdrawalInProgress(status)) {
    return 'blue';
  }
  return 'gray';
}

export function pickLatestWithdrawalId(withdrawals: WalletWithdrawalListItem[]): string | null {
  if (withdrawals.length === 0) {
    return null;
  }

  const sorted = [...withdrawals].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  return sorted[0]?.id ?? null;
}
